import type { OtoPlan } from "../../../services/objectToObjectPlanService";
import {
  flattenMatchedTagNames,
  formatAssessmentDateTime,
  formatAssessmentStatusLabel,
  formatObjectSideDetail,
  formatScoreText,
  mapBundleStatusToUi,
  mapRelevanceLevel,
  sideTypeDisplayName,
  type OtoAssessmentListItem,
  type OtoAssessmentPairItem,
  type OtoRelevanceLevel,
  type OtoRelevanceThresholds,
} from "../../../services/objectToObjectAssessmentService";

export type ObjectAnalysisPlan = {
  /** API plan id; remains the filter value sent to assessments. */
  id: string;
  /** Display: object_a.type_name */
  objectA: string;
  /** Display: object_b.type_name */
  objectB: string;
  /** Pair-list query value: object_a.type */
  objectAType: string;
  /** Pair-list query value: object_b.type */
  objectBType: string;
};

/** Bidirectional label from type_name only, e.g. "Insight ⇄ Product". */
export function formatPlanLabel(plan: ObjectAnalysisPlan): string {
  return `${plan.objectA} ⇄ ${plan.objectB}`;
}

/**
 * Keep the plan id as the option value while displaying type_name only.
 */
export function toObjectAnalysisPlan(plan: OtoPlan): ObjectAnalysisPlan {
  return {
    id: plan.id,
    objectA:
      plan.objectATypeName.trim() || sideTypeDisplayName(plan.objectAType),
    objectB:
      plan.objectBTypeName.trim() || sideTypeDisplayName(plan.objectBType),
    objectAType: plan.objectAType,
    objectBType: plan.objectBType,
  };
}

export function findPlan(
  planId: string,
  plans: readonly ObjectAnalysisPlan[],
): ObjectAnalysisPlan | undefined {
  return plans.find((plan) => plan.id === planId);
}

export type ObjectAnalysisStatus =
  | "completed"
  | "failed"
  | "partial"
  | "inProgress";

export type ObjectAnalysisSummaryRow = {
  id: string;
  assessmentId: string;
  planId: string;
  planLabel: string;
  objectATypeLabel: string;
  objectBTypeLabel: string;
  objectACount: number;
  objectAUnit: string;
  objectBCount: number;
  objectBUnit: string;
  /** Display value, e.g. "24-Jul-2026 09:14". */
  assessmentTime: string;
  /** Sortable/filterable value backing `assessmentTime`. */
  assessmentTimeIso: string;
  /** Raw API status used for display rules. */
  rawStatus: string;
  status: ObjectAnalysisStatus;
  statusLabel: string;
  completed: number;
  total: number;
};

export type ObjectAnalysisRelevance = OtoRelevanceLevel;

export type ObjectAnalysisPairRow = {
  id: string;
  assessmentId: string;
  planId: string;
  assessmentTimeIso: string;
  objectAType: string;
  objectATitle: string;
  objectADetails: string;
  objectBType: string;
  objectBTitle: string;
  objectBDetails: string;
  matchingTags: string[];
  score: string;
  relevance: ObjectAnalysisRelevance | null;
};

export type ObjectAnalysisFilter = {
  planIds: string[];
  fromIso: string | null;
  toIso: string | null;
};

function versionLabel(version: string | null | undefined): string {
  if (!version?.trim()) return "";
  const trimmed = version.trim();
  return trimmed.toUpperCase().startsWith("V") ? trimmed : `V${trimmed}`;
}

function buildEntityDetails(
  objectId: string,
  version: string | null,
): string {
  return [objectId, versionLabel(version)].filter(Boolean).join(" · ");
}

function capitalizeObjectType(objectType: string): string {
  const trimmed = objectType.trim();
  if (!trimmed) return "-";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

export function toSummaryRow(
  item: OtoAssessmentListItem,
  _plans: readonly ObjectAnalysisPlan[] = [],
): ObjectAnalysisSummaryRow {
  const objectATypeLabel = capitalizeObjectType(item.objectA.objectType);
  const objectBTypeLabel = capitalizeObjectType(item.objectB.objectType);
  const objectADetail = formatObjectSideDetail(
    item.objectA.count,
    item.objectA.objectType,
    item.objectA.objectSubType,
  );
  const objectBDetail = formatObjectSideDetail(
    item.objectB.count,
    item.objectB.objectType,
    item.objectB.objectSubType,
  );

  return {
    id: item.uuid || item.taskId,
    assessmentId: item.taskId,
    planId: item.planId,
    planLabel:
      objectATypeLabel === "-" && objectBTypeLabel === "-"
        ? item.planId || "-"
        : `${objectATypeLabel} ⇄ ${objectBTypeLabel}`,
    objectATypeLabel,
    objectBTypeLabel,
    objectACount: item.objectA.count,
    objectAUnit: objectADetail.replace(/^\d+\s*/, "") || "items",
    objectBCount: item.objectB.count,
    objectBUnit: objectBDetail.replace(/^\d+\s*/, "") || "items",
    assessmentTime: formatAssessmentDateTime(item.createdAt),
    assessmentTimeIso: item.createdAt,
    rawStatus: item.status,
    status: mapBundleStatusToUi(item.status, item.completedCount),
    statusLabel: formatAssessmentStatusLabel(
      item.status,
      item.completedCount,
      item.totalPairs,
    ),
    completed: item.completedCount,
    total: item.totalPairs,
  };
}

export function toPairRow(
  item: OtoAssessmentPairItem,
  thresholds: OtoRelevanceThresholds,
  plans: readonly ObjectAnalysisPlan[] = [],
): ObjectAnalysisPairRow {
  const plan = plans.find(
    (candidate) =>
      candidate.objectAType === item.objectAType &&
      candidate.objectBType === item.objectBType,
  );

  return {
    id: item.uuid,
    assessmentId: item.assessmentId,
    planId: plan?.id ?? "",
    assessmentTimeIso: item.createdAt,
    objectAType: capitalizeObjectType(item.objectAType),
    objectATitle: item.objectATitle?.trim() || "-",
    objectADetails: buildEntityDetails(
      item.objectAId,
      item.objectAVersion,
    ),
    objectBType: capitalizeObjectType(item.objectBType),
    objectBTitle: item.objectBTitle?.trim() || "-",
    objectBDetails: buildEntityDetails(
      item.objectBId,
      item.objectBVersion,
    ),
    matchingTags: flattenMatchedTagNames(item.matchedTags),
    score: formatScoreText(item.relevanceScore, item.totalWeight),
    relevance: mapRelevanceLevel(
      item.relevanceScore,
      item.totalWeight,
      thresholds,
    ),
  };
}

export function formatAssessmentStatus(row: ObjectAnalysisSummaryRow): string {
  return row.statusLabel;
}

function escapeCsv(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function downloadCsv(headers: string[], lines: string[], fileName: string) {
  const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function downloadObjectAnalysisSummaryCsv(
  rows: ObjectAnalysisSummaryRow[],
): void {
  const headers = [
    "Result ID",
    "Assessment Plan",
    "Object A",
    "Object B",
    "Assessment Time",
    "Assessment Status",
  ];
  const lines = rows.map((row) =>
    [
      row.assessmentId,
      row.planLabel,
      `${row.objectATypeLabel} · ${row.objectACount} ${row.objectAUnit}`,
      `${row.objectBTypeLabel} · ${row.objectBCount} ${row.objectBUnit}`,
      row.assessmentTime,
      formatAssessmentStatus(row),
    ]
      .map(escapeCsv)
      .join(","),
  );
  downloadCsv(headers, lines, "object-analysis-results.csv");
}

export function downloadObjectAnalysisPairsCsv(
  rows: ObjectAnalysisPairRow[],
): void {
  const headers = [
    "Result ID",
    "Object A",
    "Object A Details",
    "Object B",
    "Object B Details",
    "Matching Tags",
    "Score",
    "Relevance",
  ];
  const lines = rows.map((row) =>
    [
      row.assessmentId,
      row.objectAType,
      `${row.objectATitle} ${row.objectADetails}`,
      row.objectBType,
      `${row.objectBTitle} ${row.objectBDetails}`,
      row.matchingTags.join(", "),
      row.score,
      row.relevance ?? "-",
    ]
      .map(escapeCsv)
      .join(","),
  );
  downloadCsv(headers, lines, "object-analysis-pairs.csv");
}
